// Ponto unico de montagem de chave de localStorage/sessionStorage no painel.
//
// Por que existe: display_id de conversa, id de cartao, id de coluna e id de
// tarefa sao sequencias POR CONTA. Uma chave como `kanban_note_draft_42` e
// escrita pela conta 11 e lida pela conta 13 no mesmo navegador, e o texto que
// aparece na tela e o da outra conta. A auditoria de 20/09/2026 achou 12
// familias assim.
//
// Regra da casa: nenhuma tela monta string de chave na mao. Toda escrita e
// leitura de dado de cliente passa por `contaLocalStorage` ou
// `contaSessionStorage`, que anexam o id da conta e recusam gravar quando nao
// ha conta no contexto.
//
// Onde o id da conta e lido: a mesma fonte do resto do app. O getter
// `getCurrentAccountId` (store/modules/auth.js) le `route.params.accountId`, e
// a rota vem do caminho da URL. Aqui a leitura e do caminho direto, para o
// helper funcionar em service e em store, fora de componente Vue. Quem tem o
// valor em maos (Dashboard.vue) chama `definirContaAtual` e passa a mandar.

export const SEPARADOR_DE_CONTA = '::';

const PADRAO_CONTA_NA_URL = /\/accounts\/(\d+)(?:[/?#]|$)/;

let contaDefinida = null;

/**
 * Fixa a conta corrente a partir de quem ja a tem resolvida (o router).
 * Passar null volta a resolver pela URL.
 */
export const definirContaAtual = contaId => {
  const numero = Number(contaId);
  contaDefinida = Number.isInteger(numero) && numero > 0 ? numero : null;
};

const contaPelaUrl = () => {
  try {
    const achado = PADRAO_CONTA_NA_URL.exec(window.location.pathname || '');
    return achado ? Number(achado[1]) : null;
  } catch (erro) {
    return null;
  }
};

/**
 * Id da conta em que a pessoa esta agora, ou null quando nao da para saber
 * (tela de login, troca de conta em andamento, teste sem rota).
 */
export const contaAtualId = () => contaDefinida || contaPelaUrl();

/**
 * Chave ja com o id da conta. Devolve null sem conta: quem chama nao grava.
 */
export const chaveComConta = (base, contaId = contaAtualId()) => {
  if (!base || !contaId) return null;
  return `${base}${SEPARADOR_DE_CONTA}${contaId}`;
};

const semStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const pegarStorage = nome => {
  try {
    return window[nome] || semStorage;
  } catch (erro) {
    // Navegador com armazenamento bloqueado. A tela nao pode quebrar por isso.
    return semStorage;
  }
};

const criarArmazenamento = nome => ({
  /** Chave montada, util para log e teste. */
  chave: (base, contaId) => chaveComConta(base, contaId),

  getItem(base, contaId) {
    const chave = chaveComConta(base, contaId);
    if (!chave) return null;
    try {
      return pegarStorage(nome).getItem(chave);
    } catch (erro) {
      return null;
    }
  },

  setItem(base, valor, contaId) {
    const chave = chaveComConta(base, contaId);
    // Sem conta no contexto nao existe chave global de consolo: nao grava.
    if (!chave) return false;
    try {
      pegarStorage(nome).setItem(chave, valor);
      return true;
    } catch (erro) {
      return false;
    }
  },

  removeItem(base, contaId) {
    const chave = chaveComConta(base, contaId);
    if (!chave) return;
    try {
      pegarStorage(nome).removeItem(chave);
    } catch (erro) {
      // nada a limpar
    }
  },

  /** Objeto JSON inteiro, com o mesmo contrato tolerante do LocalStorage. */
  getJSON(base, padrao = null, contaId = undefined) {
    const cru = this.getItem(base, contaId);
    if (cru === null || cru === undefined) return padrao;
    try {
      return JSON.parse(cru);
    } catch (erro) {
      return padrao;
    }
  },

  setJSON(base, valor, contaId) {
    try {
      return this.setItem(base, JSON.stringify(valor), contaId);
    } catch (erro) {
      return false;
    }
  },

  // Mesmo contrato do LocalStorage do projeto (um objeto guardando varias
  // entradas), so que o objeto inteiro e da conta.
  getFromJsonStore(base, chaveInterna, contaId) {
    const armazenado = this.getJSON(base, {}, contaId) || {};
    return armazenado[chaveInterna] ?? null;
  },

  updateJsonStore(base, chaveInterna, valor, contaId) {
    const armazenado = this.getJSON(base, {}, contaId) || {};
    armazenado[chaveInterna] = valor;
    return this.setJSON(base, armazenado, contaId);
  },

  deleteFromJsonStore(base, chaveInterna, contaId) {
    const armazenado = this.getJSON(base, {}, contaId) || {};
    if (!(chaveInterna in armazenado)) return false;
    delete armazenado[chaveInterna];
    return this.setJSON(base, armazenado, contaId);
  },
});

export const contaLocalStorage = criarArmazenamento('localStorage');
export const contaSessionStorage = criarArmazenamento('sessionStorage');

// --------------------------------------------------------------------------
// Limpeza do que ja esta sujo no navegador das pessoas.
//
// As chaves antigas nao podem continuar sendo lidas nem podem ficar paradas no
// navegador. Cada familia foi decidida caso a caso:
//
//   migrar   -> texto escrito por uma pessoa, que sumir significa perder
//               trabalho. O valor antigo vira o valor da conta que estiver
//               aberta na primeira carga depois do deploy, e a chave velha sai.
//   descartar-> dado que a tela rebusca sozinha, preferencia de apresentacao,
//               passagem com validade de minutos, ou configuracao cuja
//               atribuicao errada de conta e exatamente o vazamento.
//
// A migracao roda uma vez por carga, no boot da area logada, quando o id da
// conta ja e conhecido. Ela nunca sobrescreve um valor ja escopado.
// --------------------------------------------------------------------------

// O que migra e atribuido a PRIMEIRA conta que carregar o painel depois do
// deploy, porque a chave legada nao diz de que conta ela e. Para quem so tem
// uma conta, que e todo atendente de cliente, a atribuicao esta sempre certa.
// Para quem tem varias, que aqui e o time da SiteUP, o rascumho pode aparecer
// na conta errada uma vez, e o texto e da propria pessoa. Aceitamos essa troca
// para nao apagar trabalho nao enviado no dia do deploy. Onde a atribuicao
// errada mandaria dado de um cliente para OUTRO, como no webhook do funil, a
// decisao e descartar, nao migrar (ver DESCARTAR_EXATAS).
const MIGRAR_EXATAS = [
  // Rascunho de resposta ao cliente. O mapa inteiro mora numa chave so, com
  // `draft-<display_id>-<tipo>` dentro, e display_id repete entre contas.
  { nome: 'localStorage', base: 'draftMessages' },
  // Qual mensagem esta sendo respondida, por id de conversa. Anda junto do
  // rascunho: migrar um e descartar o outro deixa a resposta sem alvo.
  { nome: 'localStorage', base: 'messageReplyTo' },
  // Filtros de funil salvos com nome dado por uma pessoa. Legado, hoje so lido
  // para oferecer a importacao para a API de visoes.
  { nome: 'localStorage', base: 'kanban_saved_filters' },
  // Presets de tarefa nomeados por uma pessoa. Mesmo caso do anterior.
  { nome: 'localStorage', base: 'tasks_presets_v1' },
];

const MIGRAR_PREFIXOS = [
  // Rascunho de nota do cartao, texto interno sobre o cliente.
  { nome: 'localStorage', prefixo: 'kanban_note_draft_' },
  // Rascunho de item de checklist do cartao.
  { nome: 'localStorage', prefixo: 'kanban_checklist_draft_' },
];

const DESCARTAR_EXATAS = [
  // URL de webhook do funil e o interruptor dela. Nao da para migrar sem
  // mentir: a configuracao foi digitada em UMA conta e a primeira carga depois
  // do deploy pode ser outra. Atribuir a conta errada e o proprio vazamento,
  // porque passaria a mandar cartao de um cliente para o endpoint de outro.
  // Sai do navegador e precisa ser digitada de novo, uma vez, por conta.
  { nome: 'localStorage', base: 'kanban_webhook_url' },
  { nome: 'localStorage', base: 'kanban_webhook_enabled' },
  // Historico de eventos com dado de cartao dentro. Registro, nao trabalho.
  { nome: 'localStorage', base: 'kanban_webhook_event_history' },
  // Rascunho sem id de cartao nenhum: nao da para saber de que cartao nem de
  // que conta e. Era lido por qualquer cartao novo, em qualquer conta.
  { nome: 'localStorage', base: 'kanban_note_draft' },
  { nome: 'localStorage', base: 'kanban_checklist_draft' },
  // Alternador de ganho/perda. Apresentacao, volta no proximo clique.
  { nome: 'localStorage', base: 'kanban_global_status_filters' },
  // Sino de tarefas: ids ja vistos. Descartar so reacende o badge, que e o
  // lado seguro do erro.
  { nome: 'localStorage', base: 'tasks_bell_seen_ids' },
  { nome: 'localStorage', base: 'tasks_bell_seen_at' },
  // Visibilidade e colapso por id de cartao. Apresentacao.
  { nome: 'localStorage', base: 'kanban_items_visibility' },
  { nome: 'localStorage', base: 'kanban_collapsed_items' },
  // Passagens de sessao com validade de 1 a 2 minutos. Ja venceram.
  { nome: 'sessionStorage', base: 'siteup:kanban-campaign-audience' },
  { nome: 'sessionStorage', base: 'siteup_task_draft_title' },
];

const DESCARTAR_PADROES = [
  // Largura, ordenacao e ajustes por id de coluna. Apresentacao.
  { nome: 'localStorage', padrao: /^kanban_column_\d+_(settings|sort|width)$/ },
];

const listarChaves = nome => {
  const storage = pegarStorage(nome);
  const chaves = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const chave = storage.key(i);
      if (typeof chave === 'string') chaves.push(chave);
    }
  } catch (erro) {
    return [];
  }
  return chaves;
};

const ehLegado = chave => !chave.includes(SEPARADOR_DE_CONTA);

const migrarUma = (nome, base, contaId) => {
  const storage = pegarStorage(nome);
  let valor = null;
  try {
    valor = storage.getItem(base);
  } catch (erro) {
    return false;
  }
  if (valor === null || valor === undefined) return false;
  const destino = chaveComConta(base, contaId);
  try {
    // Nunca sobrescreve o que a conta ja escreveu depois do deploy.
    if (destino && storage.getItem(destino) === null) {
      storage.setItem(destino, valor);
    }
    storage.removeItem(base);
    // O LocalStorage do projeto grava um carimbo `<chave>:ts` junto.
    storage.removeItem(`${base}:ts`);
  } catch (erro) {
    return false;
  }
  return true;
};

const descartarUma = (nome, base) => {
  const storage = pegarStorage(nome);
  try {
    if (storage.getItem(base) === null) return false;
    storage.removeItem(base);
    storage.removeItem(`${base}:ts`);
  } catch (erro) {
    return false;
  }
  return true;
};

/**
 * Migra o que e trabalho de uma pessoa e apaga o resto das chaves sem conta.
 * Roda uma vez por carga da area logada. Devolve o que fez, para teste e log.
 *
 * O que NAO faz: nao toca em chave que ja tem conta no nome, nao toca em chave
 * de outra origem no mesmo dominio, nao toca em preferencia de navegador sem
 * dado de cliente (tema do funil, largura de barra lateral, dispensas de
 * aviso), e nao alcanca navegador que a pessoa nunca mais abrir.
 */
export const migrarArmazenamentoParaConta = (contaId = contaAtualId()) => {
  const resultado = { migradas: [], descartadas: [] };
  if (!contaId) return resultado;

  MIGRAR_EXATAS.forEach(({ nome, base }) => {
    if (migrarUma(nome, base, contaId)) resultado.migradas.push(base);
  });

  MIGRAR_PREFIXOS.forEach(({ nome, prefixo }) => {
    listarChaves(nome)
      .filter(chave => chave.startsWith(prefixo) && ehLegado(chave))
      .forEach(chave => {
        if (migrarUma(nome, chave, contaId)) resultado.migradas.push(chave);
      });
  });

  DESCARTAR_EXATAS.forEach(({ nome, base }) => {
    if (descartarUma(nome, base)) resultado.descartadas.push(base);
  });

  DESCARTAR_PADROES.forEach(({ nome, padrao }) => {
    listarChaves(nome)
      .filter(chave => padrao.test(chave) && ehLegado(chave))
      .forEach(chave => {
        if (descartarUma(nome, chave)) resultado.descartadas.push(chave);
      });
  });

  return resultado;
};

/**
 * Apaga uma base em TODAS as contas, mais a chave legada sem conta. Usado no
 * logout, onde nao ha mais conta corrente e nada pode sobrar no navegador.
 */
export const removerEmTodasAsContas = (nome, base) => {
  const storage = pegarStorage(nome);
  const alvos = listarChaves(nome).filter(
    chave =>
      chave === base ||
      chave === `${base}:ts` ||
      chave.startsWith(`${base}${SEPARADOR_DE_CONTA}`)
  );
  alvos.forEach(chave => {
    try {
      storage.removeItem(chave);
      storage.removeItem(`${chave}:ts`);
    } catch (erro) {
      // nada a limpar
    }
  });
  return alvos;
};

let migracaoFeita = false;

/**
 * Roda a migracao uma unica vez por carga da pagina, na primeira vez que
 * alguem precisar do armazenamento. Cada consumidor chama antes da primeira
 * leitura, e o boot da area logada chama para cobrir as familias que ninguem
 * abriu nesta sessao. Sem conta ainda (tela de login) nao faz nada e deixa
 * para a proxima chamada.
 */
export const garantirMigracaoDeArmazenamento = (contaId = contaAtualId()) => {
  if (migracaoFeita || !contaId) return null;
  migracaoFeita = true;
  return migrarArmazenamentoParaConta(contaId);
};

/** So para teste: desarma a trava do `garantirMigracaoDeArmazenamento`. */
export const reiniciarMigracaoParaTeste = () => {
  migracaoFeita = false;
};

/** Lista das bases migradas e descartadas, para o spec de varredura. */
export const FAMILIAS_MIGRADAS = MIGRAR_EXATAS.map(item => item.base).concat(
  MIGRAR_PREFIXOS.map(item => item.prefixo)
);
export const FAMILIAS_DESCARTADAS = DESCARTAR_EXATAS.map(item => item.base);
